/**
 * CareCal Design System Tokens
 * Centralized design tokens for consistent theming across the application
 */

export const colors = {
  // Purple palette (primary brand color)
  blush: {
    50: '#f5f2f9',
    100: '#e9e1f3',
    200: '#d4c4e7',
    300: '#b89dd6',
    400: '#9a73c2',
    500: '#633B99', // Primary - the main brand purple
    600: '#5a3589',
    700: '#4a2c70',
    800: '#3b235a',
    900: '#2d1a45',
  },

  // Semantic colors
  white: '#FFFFFF',

  cream: '#F8F5FA',
  coral: '#FF8FA6',

  linen: '#FFFEF9',
  tigerlily: '#F8584C',
  lilac: {
    100: '#FCFBFF',
    500: '#F6F3FF',
    900: '#E6DDFF',
  },
  night: {
    100: '#E3E1E4',
    300: '#C6C3C8',
    500: '#8C8690',
    700: '#524A58',
    900: '#180D20',
  },
  avocado: '#EDF8B0',
  blueviolet: {
    light: '#7C7FCD', // hover
    DEFAULT: '#5054BC',
    dark: '#424395', // active/click
  },

  // Grayscale
  gray: {
    50: '#FAFAFA',
    100: '#F9F9F9',
    200: '#EAEAEA',
    300: '#D4D4D4',
    400: '#BCBCBC',
    500: '#9E9E9E',
    600: '#777777',
    700: '#5A5A5A',
    800: '#3D3D3D',
    900: '#2A2A2A',
  },

  // Status colors
  success: {
    light: '#F9FDE2',
    DEFAULT: '#EDF8B0', // avocado
    dark: '#589019',
  },
  warning: {
    light: '#FFF7E6',
    DEFAULT: '#EDA03A',
    dark: '#A25600',
  },
  error: { 
    light: '#FFEFED',
    DEFAULT: '#F8584C', // tigerlily
    dark: '#C04641'
  },
  info: {
    light: '#F6F3FF', // lilac-500
    DEFAULT: '#5054BC', // blueviolet
    dark: '#5054BC', // blueviolet
  },
} as const;

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  base: '16px',
  lg: '24px',
  xl: '32px',
  '2xl': '48px',
  '3xl': '64px',
} as const;

export const borderRadius = {
  sm: '8px',
  md: '12px',
  lg: '18px',
  xl: '24px',
  full: '9999px',
} as const;

export const shadows = {
  xs: '0 1px 3px rgba(99, 59, 153, 0.1)',
  sm: '0 2px 6px rgba(99, 59, 153, 0.15)',
  md: '0 4px 12px rgba(99, 59, 153, 0.2)',
  lg: '0 8px 24px rgba(99, 59, 153, 0.25)',
} as const;

export const typography = {
  fontFamily: {
    display: "'Epilogue', sans-serif",
    body: "'Nunito', sans-serif",
  },
  fontSize: {
    h1: { size: '32px', lineHeight: '38px' },
    h2: { size: '24px', lineHeight: '30px' },
    h3: { size: '20px', lineHeight: '26px' },
    h4: { size: '18px', lineHeight: '24px' },
    bodyLarge: { size: '16px', lineHeight: '24px' },
    body: { size: '14px', lineHeight: '21px' },
    bodySmall: { size: '12px', lineHeight: '18px' },
    caption: { size: '11px', lineHeight: '16px' },
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
} as const;

export const transitions = {
  fast: '150ms',
  base: '200ms',
  slow: '300ms',
} as const;

export const breakpoints = {
  mobile: '640px',
  tablet: '768px',
  desktop: '1024px',
  wide: '1280px',
} as const;

// Component-specific tokens
export const components = {
  button: {
    height: {
      sm: '36px',
      default: '44px',
      lg: '48px',
    },
    padding: {
      sm: '8px 16px',
      default: '12px 24px',
      lg: '12px 32px',
    },
    borderRadius: borderRadius.full,
  },
  input: {
    height: '44px',
    padding: '12px 16px',
    borderRadius: borderRadius.md,
    borderColor: colors.gray[200],
    focusBorderColor: colors.blush[500],
    focusRingColor: 'rgba(99, 59, 153, 0.2)',
  },
  card: {
    padding: spacing.lg,
    borderRadius: borderRadius.lg,
    backgroundColor: colors.cream,
    borderColor: 'rgba(99, 59, 153, 0.2)',
    shadow: shadows.xs,
    hoverShadow: shadows.sm,
  },
  modal: {
    borderRadius: borderRadius.xl,
    padding: spacing.lg,
    maxWidth: '512px',
    overlayColor: 'rgba(0, 0, 0, 0.6)',
  },
  toast: {
    borderRadius: borderRadius.md,
    duration: 4000, // 4 seconds
  },
} as const;

// Export type for TypeScript autocomplete
export type Colors = typeof colors;
export type Spacing = typeof spacing;
export type BorderRadius = typeof borderRadius;
export type Shadows = typeof shadows;
export type Typography = typeof typography;
export type Transitions = typeof transitions;
export type Breakpoints = typeof breakpoints;
export type Components = typeof components;
